class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    // Quantity changes inside the drawer re-render .drawer__inner via CartItems, not renderContents()
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.trackCartGap());
    }
    // Weglot only translates the initial page HTML. AJAX re-renders (add to cart, quantity change) come
    // from the Shopify locale files instead, so the footer info (theme settings) is untranslated and the
    // checkout label differs. Keep the page-load footer and the Weglot-translated label from #js-strings.
    const footerInfo = this.querySelector('.cart_info_inner');
    if (footerInfo) this.footerInfoHTML = footerInfo.innerHTML;
    new MutationObserver(() => this.restoreStaticTexts()).observe(this, { childList: true, subtree: true });
    this.restoreStaticTexts();
  }

  restoreStaticTexts() {
    const footerInfo = this.querySelector('.cart_info_inner');
    if (footerInfo && this.footerInfoHTML !== undefined && footerInfo.innerHTML !== this.footerInfoHTML) {
      footerInfo.innerHTML = this.footerInfoHTML;
    }
    const checkoutButton = this.querySelector('#CartDrawer-Checkout');
    const checkoutLabel = window.cartStrings?.checkout;
    if (checkoutButton && checkoutLabel && checkoutButton.textContent.trim() !== checkoutLabel) {
      checkoutButton.textContent = checkoutLabel;
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.trackCartGap();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('js-overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, allow_cart_drawer) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.classList.contains('is-empty') && this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if(!allow_cart_drawer) this.open();
    });
    this.trackCartGap();
  }

  // Sends the distance to the free-shipping threshold to Microsoft Clarity as a custom event.
  // Reads data-cart-gap-chf / data-cart-items from .cs_free_shipping_threshold_cart_drawer
  // (not rendered for B2B or an empty cart, then nothing is sent). Only fires when the bucket changed.
  trackCartGap() {
    if (typeof window.clarity !== 'function') return;
    const thresholdElement = this.querySelector('.cs_free_shipping_threshold_cart_drawer');
    if (!thresholdElement) return;

    const gap = parseFloat(thresholdElement.dataset.cartGapChf);
    const itemCount = parseInt(thresholdElement.dataset.cartItems, 10);
    if (Number.isNaN(gap) || Number.isNaN(itemCount)) return;

    let bucket;
    if (gap <= 0) bucket = 'CartGap_0';
    else if (gap <= 20) bucket = 'CartGap_1_20';
    else if (gap <= 50) bucket = 'CartGap_21_50';
    else bucket = 'CartGap_51plus';

    if (bucket === this.lastCartGapBucket) return;
    this.lastCartGapBucket = bucket;

    window.clarity('event', bucket);
    window.clarity('set', 'cart_items', itemCount >= 3 ? '3plus' : String(itemCount));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
